<template>
  <div class="meup-root" v-if="hasContent">
    <v-progress-linear indeterminate color="var(--color-primary)" v-if="loading" />

    <v-card class="tournament-card-preview">

      <!-- Single early-payout tooltip instance, positioned via JS -->
      <div
              class="custom-tooltip"
              :class="'tooltip-' + tooltipPlacement"
              :style="tooltipStyle"
              v-html="tooltipContent"
      ></div>

      <!-- ═══════════════════════════════ LIVE SECTION ═══════════════════════════════ -->
      <div v-if="hasLiveEvents" class="meup-section">

        <div class="market-selection-bar-desktop" v-if="!isSmallScreen && (getDynamicMarketToView || headerSignLive.length > effectiveMaxMarkets)" :style="liveGridStyle">
          <div class="grid-cell col-info spacer"></div>
          <div v-for="(i, index) in effectiveMaxMarkets" :key="'top_sel_live_' + index" class="grid-cell col-center">
            <MarketColumnDropdown
              :value="getDesktopLiveSelectedIndex(index)"
              :options="getAvailableLiveMarketsForColumn(index)"
              @change="onDesktopLiveColumnChange($event, index)"
            />
          </div>
          <div class="grid-cell col-more spacer"></div>
        </div>

        <!-- Header: category flag + tournament name + market column labels -->
        <div class="grid-row header-sub-row" :style="headerLiveGridStyle">
          <div class="grid-cell col-info path-container">
            <img
                    v-if="category && category.id"
                    :src="categoryPath + category.id + '.svg'"
                    :alt="category.name || ''"
                    @error="onImgError"
            >
            <span class="tournament-text" :title="tournament && tournament.name">
              {{ tournament && tournament.name }}
            </span>
          </div>
          <div
                  v-for="(mDef, i) in activeLiveMarketsGrid"
                  :key="'lhdr_' + i"
                  class="grid-cell col-center"
          >
            <span class="header-title">{{ mDef.marketAlias || mDef.marketName }}</span>
          </div>
          <div v-if="!isSmallScreen" class="grid-cell col-more"></div>
        </div>

        <!-- One wrapper per live event (match-top-row + grid-row pattern from MarketLivePreview) -->
        <div
                class="matches-list"
                v-for="(event, evtIdx) in uniqueLiveEvents"
                :key="'live_' + event.id"
        >
          <div class="event-row" :class="{ 'event-row--finished': event.matchFinished, 'event-row--extended': isExtendedView }">

            <!-- Status bar: current period + elapsed time + live/finished badge -->
            <div class="match-top-row">
              <div class="match-time" v-if="!isAboutToStart(event)">
                <div>
                  <span v-if="event.matchFinished" class="match-finished-badge">{{ $t('matchFinished') }}</span>
                  <span v-else class="live-badge">{{ $t('live') }}</span>
                </div>
                <div>
                  <span class="label-live-text">
                    {{ infoTimeMatch(event) }}
                  </span>
                  <span v-if="hasMatchTime(event)">
                    {{ currentTime(event.livescore.scoreboard.time) }}<span class="blink_me">'</span>
                  </span>
                </div>
              </div>
              <div class="match-time" v-else>
                <div>
                  <span class="">{{ infoTimeMatch(event) }}</span>
                </div>
                <div>

                  <Countdown :scheduled="event.scheduled" v-if="isAboutToStart(event)" />
                  <span v-else-if="hasMatchTime(event)">
                    {{ currentTime(event.livescore.scoreboard.time) }}<span class="blink_me">'</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Main grid row: competitors/score + odds columns + more button -->
            <div
                    class="grid-row"
                    :class="evtIdx % 2 === 0 ? 'even' : 'odd'"
                    :style="liveGridStyle"
            >
              <!-- col-info: competitors + scoreboard -->
              <div
                      class="grid-cell col-info match-info-container clickable-cell"
                      @click="navigateToLive(event)"
              >
                <div class="info-body-block">
                  <div class="competitors-block">
                    <div class="team-name home" :title="event.home">
                      <LogoCompetitors :team-id="event.home_id" width="18px" height="auto" />
                      <span>{{ event.home }}</span>
                      <span class="goal-ping" v-if="incidentId(event) === '9'">Goal</span>
                      <span class="var-ping"  v-if="isVarIncident(event)">VAR</span>
                    </div>
                    <div class="team-name away" :title="event.away">
                      <LogoCompetitors :team-id="event.away_id" width="18px" height="auto" />
                      <span>{{ event.away }}</span>
                      <span class="goal-ping" v-if="incidentId(event) === '9'">Goal</span>
                      <span class="var-ping"  v-if="isVarIncident(event)">VAR</span>
                    </div>
                  </div>
                  <div class="score-block">
                    <div class="match-score score-animate" :key="scoreKey(event)">
                      <ScoreboardLive :sport-id="event.sportId" :livescore="event.livescore" v-if="!isAboutToStart(event) " />
                      <span class="time" v-else>{{ formatTime(event.scheduled) }}</span>
                    </div>
                    <div
                            class="period-text"
                            v-if="event.livescore && event.livescore.scoreboard"
                    >{{ currentPeriod(event.livescore.scoreboard.currentPeriod) }}</div>
                  </div>
                </div>
              </div>

              <!-- Live odds columns -->
              <div
                      v-for="(mrktDef, rIdx) in activeLiveMarketsGrid"
                      :key="event.id + '_lc_' + rIdx"
                      class="grid-cell col-center"
              >
                <!--
                  Inline variable trick (same pattern as MarketLivePreview):
                  v-for over a single-element array produces a locally scoped `mkt` binding,
                  avoiding repeated resolveMarket() calls and enabling clean v-if/v-else.
                -->
                <template v-for="mkt in [resolveMarket(event, mrktDef)]">

                  <!-- Market found -->
                  <div
                          class="inner-grid"
                          v-if="mkt"
                          :style="innerGridStyle(mrktDef)"
                          :key="event.id + '_li_' + rIdx"
                  >
                    <!-- Specifier selector (over/under/handicap) -->
                    <template v-if="shouldShowSpecifierPlaceholder(mrktDef)">
                      <GeneralSpreadSelectorLive
                              v-if="hasSpecifier(mkt)"
                              :style="isSmallScreen ? 'grid-column: span 6;' : ''"
                              :event="event"
                              :market-def="mrktDef"
                              :resolved-market="mkt"
                              :get-alternative-lines="alternativeLinesFull"
                              :change-line="changeLine"
                              :is-odd-playable="isOddPlayable"
                              :get-specifier-type="specifierType"
                              :strip-score-state="stripScoreState"
                              :odds-sources="getOddsSources"
                              @put-cart="({ market, odd }) => addToCartLive(market, odd, event)"
                      />
                      <div
                              v-else
                              class="outcome-box"
                              :style="isSmallScreen ? 'grid-column: span 6;' : ''"
                      >
                        <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                      </div>
                    </template>

                    <!-- Standard outcomes -->
                    <template v-if="mkt.outcome && mkt.outcome.length > 0">
                      <div
                              v-if="!mkt.void"
                              class="outcome-box"
                              :style="outcomeStyle(mrktDef)"
                              v-for="odd in mkt.outcome"
                              :key="odd.unique_id || odd.id"
                      >
                        <template v-if="oddsVisualMode === 'arrows'">
                          <v-icon class="arrow-up"   v-if="odd.type_change === 'up'   && isOddPlayable(odd)">mdi-arrow-up</v-icon>
                          <v-icon class="arrow-down" v-if="odd.type_change === 'down' && isOddPlayable(odd)">mdi-arrow-down</v-icon>
                        </template>
                        <div
                                v-if="isOddPlayable(odd)"
                                class="value clickable"
                                :class="oddClasses(odd)"
                                @click.stop="addToCartLive(mkt, odd, event)"
                        >
                          <span class="outcome-name">{{ outcomeName(event.competitors, odd, mkt.unique_id) }}</span>
                          <span
                                  v-if="playerOutcomeLine(odd)"
                                  class="odd-line-val"
                                  :class="specifierType(odd.line) + '-text'"
                          >{{ playerOutcomeLine(odd) }}</span>
                          <span class="outcome-value">{{ odd.outcome_odd_value | odds_type_filter | formatDecimal }}
                            <OddBoostBadge :odd="odd" size="sm" :show-percent="false"/>
                          </span>
                        </div>
                        <div v-else class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                      </div>
                    </template>
                    <!-- No outcomes: lock placeholders -->
                    <template v-else>
                      <div
                              class="outcome-box"
                              :style="outcomeStyle(mrktDef)"
                              v-for="n in outcomeCountForMarket(mrktDef)"
                              :key="'ll_' + rIdx + '_' + n"
                      >
                        <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                      </div>
                    </template>
                  </div>

                  <!-- Market missing: lock placeholders for every expected slot -->
                  <div
                          class="inner-grid"
                          v-else
                          :style="innerGridStyle(mrktDef)"
                          :key="event.id + '_lm_' + rIdx"
                  >
                    <template v-if="shouldShowSpecifierPlaceholder(mrktDef)">
                      <div class="outcome-box" :style="isSmallScreen ? 'grid-column: span 6;' : ''">
                        <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                      </div>
                    </template>
                    <div
                            class="outcome-box"
                            :style="outcomeStyle(mrktDef)"
                            v-for="n in outcomeCountForMarket(mrktDef)"
                            :key="'lm_' + rIdx + '_' + n"
                    >
                      <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                    </div>
                  </div>

                </template>
              </div>

              <!-- +N more markets button -->
              <div v-if="!isSmallScreen" class="grid-cell col-more more-bets-container">
                <div class="outcome-box full-width">
                  <div class="value clickable more-btn" @click="navigateToLive(event)">
                    <span v-if="event.count > 0" class="count-text">+{{ event.count }}</span>
                    <v-icon small class="ml-1">mdi-chevron-right</v-icon>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
      <!-- / LIVE SECTION -->

      <!-- ═══════════════════════════ PREMATCH SECTION ═══════════════════════════ -->
      <div v-if="hasPrematchEvents" class="meup-section prematch-section">

        <div class="market-selection-bar-desktop" v-if="!isSmallScreen && (getDynamicMarketToView || headerSign.length > effectiveMaxMarkets)" :style="prematchGridStyle">
          <div class="grid-cell col-info spacer"></div>
          <div v-for="(i, index) in effectiveMaxMarkets" :key="'top_sel_pre_' + index" class="grid-cell col-center">
            <MarketColumnDropdown
              :value="getDesktopPrematchSelectedIndex(index)"
              :options="getAvailablePrematchMarketsForColumn(index)"
              @change="onDesktopPrematchColumnChange($event, index)"
            />
          </div>
          <div class="grid-cell col-more spacer"></div>
        </div>

        <div v-for="(group, dateKey) in uniquePrematchEvents" :key="'dg_' + dateKey">

          <!-- Date header row -->
          <div class="grid-row header-sub-row" :style="headerPrematchGridStyle">
            <div class="grid-cell col-info" style="align-items: center;">
              <span class="date-header-row">{{ reformatDate(group.scheduled) }}</span>
            </div>
            <div
                    v-for="(mDef, i) in activePrematchMarketsGrid"
                    :key="'phdr_' + i"
                    class="grid-cell col-center"
            >
              <span class="header-title">{{ mDef.marketAlias || mDef.marketName }}</span>
            </div>
            <div v-if="!isSmallScreen" class="grid-cell col-more"></div>
          </div>

          <!-- Prematch event rows -->
          <div
                  v-for="(event, evtIdx) in group.events"
                  :key="'pm_' + event.id"
                  class="event-row"
                  :class="{ 'event-row--extended': isExtendedView }"
          >
            <div class="match-top-row" >
              <div class="small-tourn text-truncate" :title="event.tournamentName">
                <span class="qbet" v-if="showQbet">{{event.qbet}}</span>
                <span class="cat">{{event.categoryName}}</span>
                <span>-</span>
                <span>{{ event.tournamentName }}</span>
              </div>
              <div class="time">{{ formatTime(event.scheduled) }}</div>
            </div>
            <div
                    class="grid-row"
                    :class="evtIdx % 2 === 0 ? 'even' : 'odd'"
                    :style="prematchGridStyle"
            >
              <!-- col-info: competitors + scheduled time + early-payout badge -->
              <div
                      class="grid-cell col-info match-info-container clickable-cell"
                      @click="navigateToPrematch(event)"
              >
                <div class="competitors-block">
                  <div class="team-name home">
                    <LogoCompetitors :team-id="event.home_id" width="18px" height="auto" />
                    <span>{{ event.home }}</span>
                  </div>
                  <div class="team-name away">
                    <LogoCompetitors :team-id="event.away_id" width="18px" height="auto" />
                    <span>{{ event.away }}</span>
                  </div>
                </div>
                <div class="meta-block">
                  <div class="info-market-block">
                    <div
                            v-if="hasEarlyPayoutForVisibleMarkets(event, activePrematchMarketsGrid)"
                            class="tooltip-wrapper"
                            @mouseenter="showTooltip($event, event.id, $t('early_payout_tooltip', { numberPoint: event.earlyPayoutMargin }))"
                            @mouseleave="hideTooltip"
                            @click.stop="toggleMobileTooltip($event, event.id, $t('early_payout_tooltip', { numberPoint: event.earlyPayoutMargin }))"
                    >
                      <FeatureBadge badge-key="earlyPayout" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Prematch odds columns (MarketViewV3 pattern) -->
              <div
                      v-for="(mrktDef, rIdx) in activePrematchMarketsGrid"
                      :key="event.id + '_pc_' + rIdx"
                      class="grid-cell col-center"
              >
                <!-- Same inline variable trick for prematch -->
                <template v-for="mkt in [resolveMarket(event, mrktDef)]">

                  <!-- MOBILE many-outcomes: trigger button + bottom sheet overlay -->
                  <template v-if="mkt && isSmallScreen && mkt.outcome && mkt.outcome.length > 6">
                    <button
                            class="complex-odds-trigger"
                            :class="{ 'has-selection': getComplexSelectedOdds(event, mrktDef).length > 0 }"
                            @click.stop="openComplexOdds(event.id + '_pm_' + rIdx)"
                    >
                      <template v-if="getComplexSelectedOdds(event, mrktDef).length === 0">
                        <span class="complex-odds-trigger-label">{{ mrktDef.marketAlias || mrktDef.marketName }}</span>
                      </template>
                      <template v-else-if="getComplexSelectedOdds(event, mrktDef).length === 1">
                        <span class="complex-odds-trigger-label">{{ getComplexSelectedOdds(event, mrktDef)[0].outcome_alias || getComplexSelectedOdds(event, mrktDef)[0].outcome_name }}</span>
                        <span class="complex-odds-trigger-value">{{ getComplexSelectedOdds(event, mrktDef)[0].outcome_odd_value | odds_type_filter | formatDecimal }}</span>
                      </template>
                      <template v-else>
                        <span class="complex-odds-trigger-label">{{ getComplexSelectedOdds(event, mrktDef).length }} {{ $t('selections') || 'selezioni' }}</span>
                      </template>
                      <v-icon size="16" class="complex-odds-trigger-icon">mdi-chevron-down</v-icon>
                    </button>

                    <transition name="complex-fs-slide">
                      <div v-if="complexOddsOpen[event.id + '_pm_' + rIdx]" class="complex-fs-overlay" @click.self="closeComplexOdds(event.id + '_pm_' + rIdx)">
                        <div class="complex-fs-sheet">
                          <div class="complex-fs-header">
                            <span class="complex-fs-title">{{ mrktDef.marketAlias || mrktDef.marketName }}</span>
                            <button class="complex-fs-close" @click.stop="closeComplexOdds(event.id + '_pm_' + rIdx)">
                              <v-icon>mdi-close</v-icon>
                            </button>
                          </div>
                          <div class="complex-fs-body">
                            <div class="complex-odds-grid complex-odds-grid--fullscreen">
                              <div v-for="odd in mkt.outcome" :key="odd.unique_id || odd.id" class="complex-odd-item">
                                <div
                                        v-if="isOddPlayable(odd)"
                                        class="value clickable complex-value"
                                        :class="[getOddsSources.includes(odd.unique_id) ? 'odd_selected' : '', odd.type_change]"
                                        @click.stop="addToCartPrematch(mkt, odd, event); closeComplexOdds(event.id + '_pm_' + rIdx)"
                                >
                                  <div class="cell-content">
                                    <span class="outcome-label">
                                      {{ outcomeName(event.competitors, odd, mkt.unique_id) }}
                                      <span v-if="playerOutcomeLine(odd)" class="odd-line-val ml-1" :class="specifierType(odd.line) + '-text'">{{ playerOutcomeLine(odd) }}</span>
                                    </span>
                                    <span class="outcome-value">{{ odd.outcome_odd_value | odds_type_filter | formatDecimal }}
                                      <OddBoostBadge :odd="odd" size="sm" :show-percent="false"/>
                                    </span>
                                  </div>
                                </div>
                                <div v-else class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </template>

                  <!-- DESKTOP many-outcomes OR normal market found -->
                  <div
                          class="inner-grid"
                          v-else-if="mkt"
                          :class="{ 'two-outcomes': isSmallScreen && getOutcomeCount(mrktDef) === 2, 'many-outcomes-grid': mkt.outcome && mkt.outcome.length > 6 }"
                          :style="innerGridStyle(mrktDef, mkt)"
                          :key="event.id + '_pi_' + rIdx"
                  >
                    <!--
                      Prematch specifier selector (over/under/handicap line picker).
                      Same pattern as MarketViewV3 lines 466-480.
                    -->
                    <GeneralSpreadSelectorV3
                            v-if="shouldShowSpecifierPlaceholder(mrktDef) && hasSpecifier(mkt)"
                            :event="event"
                            :marketDef="mrktDef"
                            :getEventMarket="resolveMarket"
                            :hasSpecifier="hasSpecifier"
                            :getSpecifierType="specifierType"
                            :formatLineDisplay="formatLineDisplay"
                            :getAlternativeLines="alternativeLinesForPrematch"
                            :changeLine="changeLine"
                            :isValidOdd="isOddPlayable"
                            :oddsSources="getOddsSources"
                            @put-cart="addToCartPrematch($event.market, $event.odd, event)"
                    />
                    <div
                            v-else-if="shouldShowSpecifierPlaceholder(mrktDef)"
                            class="outcome-box"
                            style="pointer-events: none;"
                    >
                      <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                    </div>

                    <!--
                      Invisible spacers (MarketViewV3 pattern):
                      right-aligns outcomes when an event has fewer outcomes than the column header expects.
                    -->
                    <div
                            class="outcome-box"
                            style="pointer-events: none; visibility: hidden;"
                            v-for="sp in spacerCount(mrktDef, mkt)"
                            :key="'sp_' + evtIdx + '_' + rIdx + '_' + sp"
                    ></div>

                    <template v-for="odd in mkt.outcome">
                      <div
                              v-if="!mkt.void"
                              class="outcome-box"
                              :key="odd.unique_id || odd.id"
                      >
                        <div
                                v-if="isOddPlayable(odd)"
                                class="value clickable"
                                :class="[getOddsSources.includes(odd.unique_id) ? 'odd_selected' : '', odd.type_change]"
                                @click.stop="addToCartPrematch(mkt, odd, event)"
                        >
                          <div class="cell-content">
                          <span
                                  v-if="playerOutcomeLine(odd)"
                                  class="outcome-label odd-line-val"
                                  :class="specifierType(odd.line) + '-text'"
                          >{{ playerOutcomeLine(odd) }}</span>
                            <span class="outcome-name">{{ outcomeName(event.competitors, odd, mkt.unique_id) }}</span>
                            <span class="outcome-value">{{ odd.outcome_odd_value | odds_type_filter | formatDecimal }}
                              <OddBoostBadge :odd="odd" size="sm" :show-percent="false"/>
                            </span>
                          </div>
                        </div>
                        <div v-else class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                      </div>
                    </template>
                  </div>

                  <!-- Market missing: locks for every expected slot -->
                  <div
                          class="inner-grid"
                          v-else
                          :style="innerGridStyle(mrktDef)"
                          :key="event.id + '_pm_' + rIdx"
                  >
                    <div
                            class="outcome-box"
                            v-for="n in outcomeCountFallback(mrktDef)"
                            :key="'pmiss_' + rIdx + '_' + n"
                    >
                      <div class="value locked"><v-icon dense small>mdi-lock</v-icon></div>
                    </div>
                  </div>

                </template>
              </div>

              <!-- +N more markets button -->
              <div v-if="!isSmallScreen" class="grid-cell col-more more-bets-container">
                <div class="outcome-box full-width">
                  <div class="value clickable more-btn" @click="navigateToPrematch(event)">+{{ event.count }}</div>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
      <!-- / PREMATCH SECTION -->

    </v-card>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { resolveEventMarket, isMarketPlayable } from '@/utils/marketLineUtils';
  import { isMobileOnly, isTablet } from 'mobile-device-detect';
  import LogoCompetitors from '@/components/center_widgets/LogoCompetitors.vue';
  import ScoreboardLive from '@/components/center_widgets/Live/previews/ScoreboardLive.vue';
  import Countdown from '@/components/center_widgets/Live/Countdown.vue';
  import GeneralSpreadSelectorLive from '@/components/center_widgets/Live/previews/GeneralSpreadSelectorLive.vue';
  import GeneralSpreadSelectorV3 from '@/components/center_widgets/Previews/spreadSelectors/GeneralSpreadSelectorV3.vue';
  import MarketColumnDropdown from '@/components/generic/MarketColumnDropdown.vue';
  import OddBoostBadge from '@/components/generic/OddBoostBadge.vue';
  import FeatureBadge from '@/components/badges/FeatureBadge.vue';
  import _ from 'lodash';

  export default {
    name: 'MixedEventsUnifiedPreview',

    components: { LogoCompetitors, ScoreboardLive, Countdown, GeneralSpreadSelectorLive, GeneralSpreadSelectorV3, MarketColumnDropdown, OddBoostBadge, FeatureBadge },

    filters: {
      formatDecimal(value) {
        if (value === undefined || value === null) return '';
        const str = String(value).trim();
        // Pass through fractional / american formats unchanged
        if (str.includes('/') || str.startsWith('+') || str.startsWith('-')) return str;
        const num = parseFloat(str);
        return isNaN(num) ? str : num.toFixed(2);
      },
    },

    props: {
      /** Flat array of live events — same shape as getMixedPreview.liveEvents */
      liveEvents: { type: Array, default: () => [] },

      /**
       * Prematch events grouped by date key.
       * Same shape as getMixedPreview.events:
       * { [dateKey]: { scheduled: String, events: Array } }
       */
      prematchEvents: { type: Object, default: () => ({}) },

      /** Market header definitions for the live odds columns */
      headerSignLive: { type: Array, default: () => [] },

      /** Market header definitions for the prematch odds columns */
      headerSign: { type: Array, default: () => [] },

      /** Tournament metadata shown in the live section header */
      tournament: { type: Object, default: null },

      /** Category metadata — used for the flag image */
      category: { type: Object, default: null },

      /** Sport metadata — passed to cart and navigation actions */
      sport: { type: Object, default: null },

      /** Show loading indicator */
      loading: { type: Boolean, default: false },
    },

    data: () => ({
      isMobileOnly,
      isTablet,
      isSmallScreen: false,
      maxMarkets: 3,
      infoColWidth: '350px',
      oddsVisualMode: 'blink',
      categoryPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/categories/round_flags/',
      // Keyed by `${eventId}_${marketId}` — tracks user-selected specifier overrides
      customLines: {},
      // Tooltip state
      activeTooltipId: null,
      activeMobileTooltipId: null,
      tooltipContent: '',
      tooltipStyle: {},
      tooltipPlacement: 'above',
      desktopLiveMarketIndices: [0, 1, 2],
      desktopPrematchMarketIndices: [0, 1, 2],
      complexOddsOpen: {},
    }),

    computed: {
      ...mapGetters({
        getEnableEarlyPayout: 'settings/getEnableEarlyPayout',
        getOddsSources: 'cart/getOddsSources',
        getLanguage:    'settings/getLanguage',
        getCashier:     'user/getCashier',
        getTerminal:     'user/getTerminal',
        getType:        'user/getType',
        getEnableQuickBetOnlyForAgency: 'settings/getEnableQuickBetOnlyForAgency',
        getEventViewMode: 'ui/getEventViewMode',
        getCompetitorsInOutcome: 'settings/getCompetitorsInOutcome',
        getDynamicMarketToView: 'settings/getDynamicMarketToView',
      }),

      effectiveMaxMarkets() {
        return this.getDynamicMarketToView ? 2 : this.maxMarkets;
      },

      isExtendedView() {
        return this.isSmallScreen && this.getEventViewMode === 'extended';
      },

      showQbet(){
        if(this.getEnableQuickBetOnlyForAgency){
          return this.getCashier || this.getType==4 || this.getTerminal
        }
        return true
      },

      uniqueLiveEvents() {
        const seen = new Set();
        return (this.liveEvents || []).filter(e => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });
      },
      uniquePrematchEvents() {
        const result = {};
        for (const [dateKey, group] of Object.entries(this.prematchEvents || {})) {
          const seen = new Set();
          result[dateKey] = {
            ...group,
            events: (group.events || []).filter(e => {
              if (seen.has(e.id)) return false;
              seen.add(e.id);
              return true;
            })
          };
        }
        return result;
      },

      hasLiveEvents()    { return this.uniqueLiveEvents.length > 0; },
      hasPrematchEvents() { return Object.keys(this.uniquePrematchEvents).length > 0; },
      hasContent()       { return this.hasLiveEvents || this.hasPrematchEvents; },

      /** Live market column definitions, capped by screen breakpoint */
      activeLiveMarketsGrid() {
        if (!this.headerSignLive || this.headerSignLive.length === 0) return [];
        if (this.isSmallScreen) return this.headerSignLive.slice(0, 1);

        const selectedMarkets = [];
        for (let i = 0; i < this.effectiveMaxMarkets; i++) {
          let targetIndex = this.desktopLiveMarketIndices[i] !== undefined ? this.desktopLiveMarketIndices[i] : i;
          if (targetIndex >= this.headerSignLive.length) targetIndex = i < this.headerSignLive.length ? i : 0;
          selectedMarkets.push(this.headerSignLive[targetIndex]);
        }
        return selectedMarkets;
      },

      /** Prematch market column definitions, capped by screen breakpoint */
      activePrematchMarketsGrid() {
        if (!this.headerSign || this.headerSign.length === 0) return [];
        if (this.isSmallScreen) return this.headerSign.slice(0, 1);

        const count = Math.min(this.effectiveMaxMarkets, this.headerSign.length);
        const selectedMarkets = [];
        for (let i = 0; i < count; i++) {
          let targetIndex = this.desktopPrematchMarketIndices[i] !== undefined ? this.desktopPrematchMarketIndices[i] : i;
          if (targetIndex >= this.headerSign.length) targetIndex = i < this.headerSign.length ? i : 0;
          selectedMarkets.push(this.headerSign[targetIndex]);
        }
        return selectedMarkets;
      },


      headerLiveGridStyle(){

        const info = this.isSmallScreen ? '42%' : this.infoColWidth;
        const cols = this.isSmallScreen
                ? `${info} 1fr`
                : `${info} repeat(${this.activeLiveMarketsGrid.length}, minmax(0, 1fr)) 60px`;
        return {
          display: 'grid',
          'grid-template-columns': cols,
          'align-items': 'stretch',
          'border-bottom': '1px solid var(--border-color)',
          width: '100%',
        };

      },

      headerPrematchGridStyle(){

        const info = this.isSmallScreen ? '42%' : this.infoColWidth;
        const cols = this.isSmallScreen
                ? `${info} 1fr`
                : `${info} repeat(${this.activePrematchMarketsGrid.length}, minmax(0, 1fr)) 60px`;
        return {
          display: 'grid',
          'grid-template-columns': cols,
          'align-items': 'stretch',
          'border-bottom': '1px solid var(--border-color)',
          width: '100%',
        };

      },

      /** CSS grid style for live section rows */
      liveGridStyle() {
        return this._buildGridStyle(this.activeLiveMarketsGrid.length);
      },

      /** CSS grid style for prematch section rows */
      prematchGridStyle() {
        return this._buildGridStyle(this.activePrematchMarketsGrid.length);
      },
    },

    methods: {
      /**
       * Early-payout badge visibility, scoped to the markets CURRENTLY on screen.
       *
       * `event.earlyPayout` only means the event is eligible in principle — a rule
       * resolved for its sport/category/tournament. WHICH outcomes actually qualify
       * is `event.earlyPayoutOutcomes`, a whitelist of (marketId, outcomeName)
       * pairs, and for football today that is only market 1 (1X2), outcomes 1 and 2.
       *
       * Keying the badge on `event.earlyPayout` alone leaves it lit while the market
       * selector is on Double Chance, Under/Over and the rest, promising a feature
       * the bet slip then refuses: `cartHandler.refactorOddV3` matches this same
       * whitelist and sets `odd.earlyPayout = false` for anything outside it.
       *
       * The caller passes whatever it currently has on screen, because each layout
       * names that differently — visibleMarkets(object) in the MarketViewV3 family,
       * `activePrematchMarketsGrid` here, whose columns come from the two
       * MarketColumnDropdown selectors via `desktopPrematchMarketIndices`.
       *
       * On the Main tab several markets share the row, so the badge shows when AT
       * LEAST ONE of them qualifies — which is why 1X2 keeps lighting it there.
       *
       * Market granularity, not outcome: the badge sits in the event meta block, not
       * on the outcome buttons, so it cannot distinguish 1X2's eligible "1"/"2" from
       * its ineligible "X".
       *
       * An empty or missing whitelist hides the badge. That is deliberate: it also
       * covers a sport with no rows in feed_earlyPayoutMarket, where the event flag
       * is true but no outcome can ever be cashed out early. Better to show nothing
       * than to promise what the bet slip will refuse.
       */
      hasEarlyPayoutForVisibleMarkets(event, visibleMarkets) {
          if (!this.getEnableEarlyPayout) return false;
          if (!event || !event.earlyPayout || !event.earlyPayoutMargin) return false;

          const outcomes = event.earlyPayoutOutcomes || [];
          if (outcomes.length === 0) return false;

          const visible = visibleMarkets || [];
          if (visible.length === 0) return false;

          const eligibleMarketIds = new Set(outcomes.map(o => String(o.marketId)));
          return visible.some(m => m && eligibleMarketIds.has(String(m.marketId)));
      },

      ...mapActions({
        addOddToCartV3:         'cart/addOddToCartV3',
        fetchEventDetailV3:     'detailPrematch/fetchEventDetailV3',
        fetchEventLiveDetailV5: 'detailLive/fetchEventLiveDetailV5',
        setLoadingPreview:      'odds/setLoadingPreview',
        closeTopsportsPanel:    'topsports/closePanel',
        setViewMode:    'ui/setViewMode',
      }),
      // --- HELPER SELEZIONE MERCATI LIVE ---
      getDesktopLiveSelectedIndex(colIdx) {
        return this.desktopLiveMarketIndices[colIdx] !== undefined ? this.desktopLiveMarketIndices[colIdx] : colIdx;
      },
      onDesktopLiveColumnChange(newVal, colIdx) {
        const val = parseInt(newVal, 10);
        if (!isNaN(val)) this.$set(this.desktopLiveMarketIndices, colIdx, val);
      },
      getAvailableLiveMarketsForColumn(colIdx) {
        const source = this.headerSignLive || [];
        const currentSelection = Array.from({length: this.effectiveMaxMarkets}).map((_, i) =>
                this.desktopLiveMarketIndices[i] !== undefined ? this.desktopLiveMarketIndices[i] : i
        );
        return source.map((market, idx) => ({
          ...market, originalIndex: idx,
          disabled: currentSelection.includes(idx) && currentSelection[colIdx] !== idx
        }));
      },

      // --- HELPER SELEZIONE MERCATI PREMATCH ---
      getDesktopPrematchSelectedIndex(colIdx) {
        return this.desktopPrematchMarketIndices[colIdx] !== undefined ? this.desktopPrematchMarketIndices[colIdx] : colIdx;
      },
      onDesktopPrematchColumnChange(newVal, colIdx) {
        const val = parseInt(newVal, 10);
        if (!isNaN(val)) this.$set(this.desktopPrematchMarketIndices, colIdx, val);
      },
      getAvailablePrematchMarketsForColumn(colIdx) {
        const source = this.headerSign || [];
        const currentSelection = Array.from({length: this.effectiveMaxMarkets}).map((_, i) =>
                this.desktopPrematchMarketIndices[i] !== undefined ? this.desktopPrematchMarketIndices[i] : i
        );
        return source.map((market, idx) => ({
          ...market, originalIndex: idx,
          disabled: currentSelection.includes(idx) && currentSelection[colIdx] !== idx
        }));
      },

      ...mapActions('ui', ['setSubaction']),

      // ─── Grid / layout builders ────────────────────────────────────────────────

      _buildGridStyle(columnCount) {
        if (this.isExtendedView) {
          return {
            display: 'flex',
            'flex-direction': 'column',
            'border-bottom': '1px solid var(--border-color)',
            width: '100%',
            gap: '6px',
          };
        }
        const info = this.isSmallScreen ? '42%' : this.infoColWidth;
        const cols = this.isSmallScreen
                ? `${info} 1fr`
                : `${info} repeat(${columnCount}, minmax(0, 1fr)) 60px`;
        return {
          display: 'grid',
          'grid-template-columns': cols,
          'align-items': 'stretch',
          'border-bottom': '1px solid var(--border-color)',
          width: '100%',
        };
      },

      /** Inner grid for a single odds-column cell (accounts for specifier placeholder) */
      innerGridStyle(mrktDef, mkt = null) {
        // Many-outcomes market (e.g. goalscorers): wrap into a 4-column grid
        if (mkt && mkt.outcome && mkt.outcome.length > 6) {
          return { display: 'grid', 'grid-template-columns': 'repeat(4, minmax(0, 1fr))', 'align-items': 'center', width: '100%', height: 'auto', gap: '4px', padding: '4px 0' };
        }
        const hasSpec = this.shouldShowSpecifierPlaceholder(mrktDef);
        const count = mrktDef && mrktDef.outcomeNames
                ? mrktDef.outcomeNames.length
                : (mrktDef && mrktDef.outcomeNumbers ? mrktDef.outcomeNumbers : 3);

        if (hasSpec && this.isSmallScreen) {
          return { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', alignItems: 'center', width: '100%', height: '100%', gap: '4px', padding: '0' };
        }
        const cols = (hasSpec ? '45px ' : '') + `repeat(${count}, minmax(0, 1fr))`;
        return { display: 'grid', 'grid-template-columns': cols, 'align-items': 'center', width: '100%', height: '100%', gap: '4px', padding: '0' };
      },

      /** Grid-column span for individual outcome boxes when a specifier col is present */
      outcomeStyle(mrktDef) {
        if (this.shouldShowSpecifierPlaceholder(mrktDef) && this.isSmallScreen) {
          const len = mrktDef.outcomeNames ? mrktDef.outcomeNames.length : (mrktDef.outcomeNumbers || 3);
          if (len === 2) return { gridColumn: 'span 3' };
          if (len === 3) return { gridColumn: 'span 2' };
        }
        return {};
      },

      // ─── Market resolution ─────────────────────────────────────────────────────

      /**
       * Resolves the correct market line for a given event + market header definition.
       * Applies any user-selected custom specifier stored in `customLines`.
       */
      resolveMarket(event, marketDef) {
        if (!event.odds || !marketDef) return null;
        const mId = String(marketDef.marketId || marketDef.id);
        const matches = event.odds.filter(m => String(m.id || m.marketId) === mId);
        return resolveEventMarket(matches, this.customLines[`${event.id}_${mId}`] || null) || null;
      },

      // ─── Specifier helpers ─────────────────────────────────────────────────────

      /** Returns true when the market header represents an over/under or handicap market */
      shouldShowSpecifierPlaceholder(headerMarket) {
        if (!headerMarket) return false;
        if (headerMarket.marketWithSpecifier === true)  return true;
        if (headerMarket.marketWithSpecifier === false) return false;
        // Fallback: word-boundary regex avoids false positives (e.g. "overtime")
        const name = (headerMarket.marketName || '').toLowerCase();
        return /\bunder\b|\bover\b|\bhandicap\b|\bgoal\b/.test(name);
      },

      hasSpecifier(mkt) {
        return mkt && mkt.specifier && mkt.specifier !== '' && mkt.specifier !== 'noSpec';
      },

      specifierType(specifier) {
        if (specifier == null || String(specifier) === '') return 'neutral';
        const s = String(specifier).trim();
        if (s.startsWith('-')) return 'negative';
        if (s.startsWith('+')) return 'positive';
        return 'neutral';
      },

      stripScoreState(str) {
        return str ? String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim() : str;
      },

      playerOutcomeLine(odd) {
        if (!odd.line) return null;
        if (odd.baseLine) {
          if (odd.line === odd.baseLine) return null;
          if (odd.line.startsWith(odd.baseLine + '#')) return odd.line.slice(odd.baseLine.length + 1);
          // Hide mirror handicap: "6.5 (0-0)" vs baseLine "-6.5 (0-0)" — same value, opposite sign
          const sLine = String(odd.line).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();
          const sBase = String(odd.baseLine).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();
          const nLine = parseFloat(sLine);
          const nBase = parseFloat(sBase);
          if (!isNaN(nLine) && !isNaN(nBase) && nLine === -nBase) return null;
        }
        return odd.line.replace('#', ' · ');
      },

      /** Returns alternative specifier lines for the GeneralSpreadSelectorLive component */
      alternativeLinesFull(event, resolvedMarket) {
        if (!event.odds || !resolvedMarket) return [];
        const mId = String(resolvedMarket.id || resolvedMarket.marketId);
        return event.odds
                .filter(m =>
                        String(m.id || m.marketId) === mId &&
                        String(m.specifier) !== String(resolvedMarket.specifier) &&
                        m.outcome && m.outcome.some(o => this.isOddPlayable(o))
                )
                .sort((a, b) => parseFloat(a.specifier) - parseFloat(b.specifier));
      },

      /**
       * Alternative lines for prematch GeneralSpreadSelectorV3.
       * Includes all playable variants (including current line) — same logic as MarketViewV3.getAlternativeLines.
       */
      alternativeLinesForPrematch(event, currentMarket) {
        if (!event.odds || !currentMarket) return [];
        const mId = String(currentMarket.id || currentMarket.marketId);
        return event.odds
                .filter(m => String(m.id || m.marketId) === mId && isMarketPlayable(m))
                .sort((a, b) => parseFloat(a.specifier) - parseFloat(b.specifier));
      },

      /**
       * Formats a specifier value for display in the line selector button.
       * Adds '+' prefix for positive handicap values — mirrors MarketViewV3.formatLineDisplay.
       */
      formatLineDisplay(line, marketDef) {
        if (line == null || String(line) === '') return '';
        const str = this.stripScoreState(String(line).trim());
        // Prefix positive handicap specifiers with '+' (over/under values stay as-is)
        const isHandicap = /\bhandicap\b/i.test(marketDef.marketName || '');
        if (isHandicap && /^[0-9]/.test(str) && parseFloat(str) !== 0) {
          return '+' + str;
        }
        return str;
      },

      changeLine(event, marketOrDef, newSpecifier) {
        const mId = String(marketOrDef.marketId || marketOrDef.id);
        this.$set(this.customLines, `${event.id}_${mId}`, String(newSpecifier));
      },

      // ─── Odd helpers ───────────────────────────────────────────────────────────

      isOddPlayable(odd) {
        return odd && odd.active && parseFloat(odd.outcome_odd_value) > 1;
      },

      oddClasses(odd) {
        const classes = [];
        if (this.getOddsSources.includes(odd.unique_id)) classes.push('odd_selected');
        if (this.oddsVisualMode === 'blink') {
          if (odd.type_change === 'up')   classes.push('odd_blink_up');
          else if (odd.type_change === 'down') classes.push('odd_blink_down');
        }
        return classes;
      },

      outcomeName(competitors, odd, unique_id) {
        const name = odd.outcome_name?.toLowerCase();
        if (odd.outcome_name === 'X') return odd.outcome_alias || odd.outcome_name;
        if (this.getCompetitorsInOutcome) {
          if (name === 'local')     return competitors?.[0]?.name || odd.outcome_alias || odd.outcome_name;
          if (name === 'visitante') return competitors?.[1]?.name || odd.outcome_alias || odd.outcome_name;
          if ((odd.outcome_name === '1' || odd.outcome_name === '2') && unique_id != '128' && competitors) {
            for (const c of competitors) {
              if (c.position === odd.outcome_name) return c.name;
            }
          }
        }
        return odd.outcome_alias || odd.outcome_name;
      },

      // ─── Outcome count helpers ─────────────────────────────────────────────────

      outcomeCountForMarket(mrktDef) {
        if (!mrktDef) return 3;
        return mrktDef.outcomeNames ? mrktDef.outcomeNames.length : (mrktDef.outcomeNumbers || 3);
      },

      // Alias used by the prematch two-outcomes computed class
      getOutcomeCount(mrktDef) { return this.outcomeCountForMarket(mrktDef); },

      /** Fallback lock count for missing markets — adds 1 slot when a specifier col is expected */
      outcomeCountFallback(mrktDef) {
        let count = mrktDef && mrktDef.outcomeNames ? mrktDef.outcomeNames.length : 3;
        if (this.shouldShowSpecifierPlaceholder(mrktDef)) count += 1;
        return count;
      },

      /** Number of invisible spacer boxes needed to right-align outcomes (MarketViewV3 pattern) */
      spacerCount(mrktDef, mkt) {
        if (!mkt || !mrktDef) return 0;
        const expected = (mrktDef.outcomeNames || []).length;
        const actual   = (mkt.outcome || []).length;
        return Math.max(0, expected - actual);
      },

      // ─── Live state helpers ────────────────────────────────────────────────────

      isAboutToStart(event) {
        return event.status === '9' ;
      },

      hasMatchTime(event) {
        const t = event.livescore?.scoreboard?.time;
        return t && t !== '-1' && t !== '0';
      },

      incidentId(event) {
        return event.livescore?.currentIncident?.id;
      },

      isVarIncident(event) {
        const id = this.incidentId(event);
        return id === '1074' || id === '7' || id === '6';
      },

      infoTimeMatch(event) {
        const { livescore, sportId, status } = event;
        const period = livescore?.scoreboard?.currentPeriod;
        if (period && period !== '-1') return this.$t('periods.' + sportId + '.' + period);
        if (status === '9' || period === '-1') return this.$t('periods.about_to_start');
        return '';
      },

      currentTime(time) {
        if (time == null || time === '' || time === '-1') return '';
        const t = parseInt(time, 10);
        if (isNaN(t) || t < 0) return '';
        const m = Math.floor(t / 60), s = t - m * 60;
        return m + ':' + (s < 10 ? '0' : '') + s;
      },

      currentPeriod(period) {
        if (!period) return '';
        if (Array.isArray(period)) {
          const found = _.find(period, { lang: this.getLanguage }) || _.find(period, { lang: 'en' });
          return found ? found.text : '';
        }
        return '';
      },

      scoreKey(event) {
        const r = event.livescore?.scoreboard?.results;
        if (!r) return event.id + '_0-0';
        const h = r.find(x => x.position == '1')?.value || 0;
        const a = r.find(x => x.position == '2')?.value || 0;
        return `${event.id}_${h}-${a}`;
      },

      // ─── Cart actions ──────────────────────────────────────────────────────────

      async addToCartLive(mkt, odd, event) {
        if (!this.isOddPlayable(odd)) return;
        odd.type     = 'live';
        odd.provider = event.provider || 'lsport';
        if (navigator.vibrate) navigator.vibrate(20);
        await this.addOddToCartV3({
          oddMarket: mkt, odd, event,
          sport:      this.sport,
          category:   this.category,
          tournament: this.tournament,
          type: 'live',
        });
        this.$root.$emit('forceUpdateSingleMultiple');
      },

      async addToCartPrematch(mkt, odd, event) {
        if (!this.isOddPlayable(odd)) return;
        odd.provider = event.provider || 'lsport';
        if (navigator.vibrate) navigator.vibrate(20);
        await this.addOddToCartV3({
          oddMarket: mkt, odd, event,
          sport:      this.sport,
          category:   this.category,
          tournament: this.tournament,
        });
        this.$root.$emit('forceUpdateSingleMultiple');
      },

      // ─── Navigation ────────────────────────────────────────────────────────────

      navigateToLive(event) {
        this.closeTopsportsPanel();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.parent.postMessage({
          type:          'eventOpened',
          eventId:       event.id,
          sportId:       event.sportId,
          tournamentId:  event.tournamentId || '',
          eventName:     `${event.home} - ${event.away}`,
          eventNameBack: null,
          section:       'live',
        }, '*');
        this.setViewMode({ mode: 'detail' });

        this.fetchEventLiveDetailV5({
          source:     event.id,
          group:      0,
          sport:      event.sportId,
          tournament: event.tournamentId || '',
          status:     event.status,
          sportName:        event.sportName,
          categoryName:     event.categoryName,
          tournamentName:   event.tournamentName,
          competitorHome:   event.home,
          competitorAway:   event.away,
          competitorHomeId: event.home_id,
          competitorAwayId: event.away_id,
        });
      },

      navigateToPrematch(event) {
        this.closeTopsportsPanel();
        window.parent.postMessage({
          type:          'eventOpened',
          eventId:       event.id,
          sportId:       this.sport?.id,
          tournamentId:  this.tournament?.id,
          eventName:     `${event.home} - ${event.away}`,
          eventNameBack: null,
          section:       'prematch',
        }, '*');
        if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        this.setLoadingPreview({ status: true });
        this.fetchEventDetailV3({
          source:          event.id,
          group:           null,
          sport:           this.sport?.id,
          category:        this.category?.id,
          tournament:      this.tournament?.id,
          status:          event.sport_event_status?._attributes?.status || event.status,
          startEventTime:  event.scheduled,
          competitorHome:  event.home,
          competitorAway:  event.away,
          sportName:      event.sportName,
          categoryName:   event.categoryName,
          tournamentName: event.tournamentName,
          competitorHomeId: event.home_id,
          competitorAwayId: event.away_id,

        }).then(() => this.setLoadingPreview({ status: false }));
      },

      // ─── Date / time formatting ────────────────────────────────────────────────

      reformatDate(date) {
        if (!date) return '';
        const d = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + 'T12:00:00') : new Date(date);
        return d.toLocaleDateString([], { weekday: 'short', day: '2-digit', month: '2-digit' });
      },

      formatTime(date) {
        return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },

      // ─── Tooltip ──────────────────────────────────────────────────────────────

      positionTooltip(rect) {
        this.$nextTick(() => {
          const el = this.$el?.querySelector('.custom-tooltip');
          if (!el) return;
          const tipH = el.offsetHeight;
          const tipW = el.offsetWidth;
          const vw   = window.innerWidth;
          const gap  = 8;
          const above = rect.top - tipH - gap >= 0;
          const top   = above ? rect.top - gap : rect.bottom + gap;
          this.tooltipPlacement = above ? 'above' : 'below';
          const rawLeft = rect.left + rect.width / 2;
          let left, transformX;
          if      (rawLeft - tipW / 2 < gap)          { left = gap;              transformX = '0%'; }
          else if (rawLeft + tipW / 2 > vw - gap)     { left = vw - gap - tipW; transformX = '0%'; }
          else                                         { left = rawLeft;          transformX = '-50%'; }
          this.tooltipStyle = {
            top: top + 'px', left: left + 'px',
            transform: `translateX(${transformX}) translateY(${above ? '-100%' : '0%'})`,
            opacity: '1',
          };
        });
      },

      showTooltip(nativeEvent, eventId, content) {
        this.tooltipContent = content;
        this.tooltipStyle   = { top: '-9999px', left: '-9999px' };
        this.activeTooltipId = eventId;
        this.positionTooltip(nativeEvent.currentTarget.getBoundingClientRect());
      },

      hideTooltip() {
        this.activeTooltipId = null;
        this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
      },

      toggleMobileTooltip(nativeEvent, eventId, content) {
        if (this.activeMobileTooltipId === eventId) {
          this.activeMobileTooltipId = null;
        } else {
          this.activeMobileTooltipId = eventId;
          this.showTooltip(nativeEvent, eventId, content);
        }
      },

      // ─── Complex odds sheet (mobile many-outcomes) ────────────────────────────

      openComplexOdds(key) {
        this.$set(this.complexOddsOpen, key, true);
        document.body.classList.add('no-scroll');
      },

      closeComplexOdds(key) {
        this.$set(this.complexOddsOpen, key, false);
        if (!Object.values(this.complexOddsOpen).some(v => v)) {
          document.body.classList.remove('no-scroll');
        }
      },

      getComplexSelectedOdds(event, mrktDef) {
        const market = this.resolveMarket(event, mrktDef);
        if (!market || !market.outcome) return [];
        return market.outcome.filter(o => this.getOddsSources.includes(o.unique_id));
      },

      // ─── Image error ───────────────────────────────────────────────────────────

      onImgError(e) { e.target.style.display = 'none'; },

      // ─── Responsive ────────────────────────────────────────────────────────────

      onResize() {
        const w = window.innerWidth;
        this.isSmallScreen = w <= 600;
        this.$nextTick(this.updateCatsArrows);
        this.$nextTick(this.updateSportsArrows);

        // MOSTRA 3 MARKET SOLO SE LO SCHERMO È MAGGIORE O UGUALE A 1650px
        if (w >= 1650) {
          this.maxMarkets = 3;
          this.infoColWidth = '350px';
          this.moreColWidth = '50px';
        }
        // SE LO SCHERMO È INFERIORE A 1650px (MA MAGGIORE DI 1000px), MOSTRA 2 MARKET
        else if (w > 1000) {
          this.maxMarkets = 2;
          this.infoColWidth = '300px';
          this.moreColWidth = '50px';
        }
        else if (w > 600) {
          this.maxMarkets = 1;
          this.infoColWidth = '250px';
          this.moreColWidth = '50px';
        }
        else {
          this.maxMarkets = 1;
          this.infoColWidth = '50%';
          this.moreColWidth = '30px';
        }
      },
    },

    mounted() {
      this.onResize();
      window.addEventListener('resize', this.onResize);
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },
  };
</script>

<style scoped>
  /* ── Layout grid ──────────────────────────────────────────────────────────── */
  .meup-root { width: 100%; }

  .grid-row { display: grid; align-items: stretch; }
  .grid-cell { display: flex; align-items: center; }
  .col-info  { padding: 0 10px; justify-content: space-between; }
  .col-center { justify-content: center; }
  .col-more  { justify-content: center; border-right: none; }
  .header-title { color: var(--text-platform); font-weight: bold; font-size: var(--font-size-xl); }

  /* ── Inner odds grid ──────────────────────────────────────────────────────── */
  .inner-grid { display: grid; align-items: center; width: 100%; height: 100%; gap: 4px; }

  /* ── Many-outcomes grid (goalscorers, 100+ odds, etc.) ────────────────────── */
  .many-outcomes-grid { height: auto !important; }
  .many-outcomes-grid .outcome-box { height: 36px; min-height: 36px; }
  .many-outcomes-grid .outcome-box .outcome-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }

  /* ── Complex odds grid (desktop) ──────────────────────────────────────────── */
  .complex-odds-grid { display: flex; flex-wrap: wrap; gap: 5px; width: 100%; }
  .complex-odd-item { flex: 1; min-width: 128px; height: 36px; }
  .complex-odd-item .outcome-label { overflow: visible; max-width: none; white-space: normal; text-overflow: unset; }
  .complex-value { width: 100%; height: 100%; }

  /* ── Mobile trigger button ─────────────────────────────────────────────────── */
  .complex-odds-trigger { height: 100%; min-height: 44px; display: flex; align-items: center; gap: 6px; width: 100%; padding: 8px 10px; background: var(--bg-odd, #1a1a1a); border: 1px solid var(--border-color, #333); border-radius: 6px; color: var(--text-platform); font-size: var(--font-size-2xl); font-weight: 600; cursor: pointer; text-align: left; }
  .complex-odds-trigger.has-selection { border-color: var(--color-primary); background: rgba(0, 96, 57, 0.12); }
  .complex-odds-trigger-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .complex-odds-trigger-value { color: var(--color-primary); font-weight: 700; font-size: var(--font-size-3xl); flex-shrink: 0; }
  .complex-odds-trigger-icon { flex-shrink: 0; opacity: 0.7; }

  /* ── Bottom sheet overlay ──────────────────────────────────────────────────── */
  .complex-fs-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); z-index: 300; display: flex; flex-direction: column; justify-content: flex-end; }
  .complex-fs-sheet { display: flex; flex-direction: column; width: 100%; max-height: 70vh; background: var(--bg-panel, #121212); border-radius: 16px 16px 0 0; overflow: hidden; }
  .complex-fs-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid var(--border-color, #333); flex-shrink: 0; }
  .complex-fs-title { font-size: var(--font-size-5xl); font-weight: 700; color: var(--text-platform); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-right: 12px; }
  .complex-fs-close { background: transparent; border: none; cursor: pointer; color: var(--text-platform); padding: 4px; display: flex; align-items: center; }
  .complex-fs-body { flex: 1; overflow-y: auto; padding: 12px 12px 90px 12px; -webkit-overflow-scrolling: touch; }
  .complex-odds-grid--fullscreen { gap: 8px; }
  .complex-odds-grid--fullscreen .complex-odd-item { min-height: 44px; }

  /* ── Slide-up from bottom transition ──────────────────────────────────────── */
  .complex-fs-slide-enter-active { transition: opacity 0.3s ease; }
  .complex-fs-slide-leave-active { transition: opacity 0.25s ease; }
  .complex-fs-slide-enter-active .complex-fs-sheet { transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1); }
  .complex-fs-slide-leave-active .complex-fs-sheet { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
  .complex-fs-slide-enter, .complex-fs-slide-leave-to { opacity: 0; }
  .complex-fs-slide-enter .complex-fs-sheet, .complex-fs-slide-leave-to .complex-fs-sheet { transform: translateY(100%); }

  /* ── Outcome box + value button ───────────────────────────────────────────── */
  .outcome-box {
    display: flex; align-items: center; justify-content: center;
    height: 100%;
    border-right: 1px solid var(--border-color);
  }
  .outcome-box:last-child { border-right: none; }



  /* ── More-markets button ──────────────────────────────────────────────────── */
  .more-btn { font-size: var(--font-size-xl); font-weight: 700; color: var(--text-op-50-platform); background: transparent; border: none; }
  .more-bets-container { padding: 4px; }
  .full-width { width: 100%; }
  .count-text { font-size: var(--font-size-xl); color: var(--text-op-50-platform); font-weight: bold; }

  /* ── Outcome label tokens ─────────────────────────────────────────────────── */
  .outcome-name  { font-size: var(--font-size-md); color: var(--text-op-50-platform); font-weight: 500; line-height: 1.2; }
  .outcome-value { font-size: var(--font-size-3xl); font-weight: 700; }
  .odd-line-val  { font-size: var(--font-size-md); font-weight: 600; line-height: 1.2; }
  .positive-text { color: var(--bg-odd-up,   #2e7d32); }
  .negative-text { color: var(--bg-odd-down, #c62828); }
  .neutral-text  { color: var(--text-op-50-platform); }

  /* ── Arrow direction icons ────────────────────────────────────────────────── */

  /* ── Competitors block ────────────────────────────────────────────────────── */
  .competitors-block {
    flex: 1; min-width: 0; padding-right: 5px;
    display: flex; flex-direction: column; justify-content: center; gap: 8px;
  }
  .team-name {
    font-weight: 500; font-size: var(--font-size-3xl); line-height: 18px;
    color: var(--text-platform);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    display: flex; align-items: center; gap: 6px; width: 100%;
    transition: color 0.2s;
  }
  .team-name img  { width: 16px; height: auto; flex-shrink: 0; }
  .team-name span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex-shrink: 1; }
  /*@media (hover: hover) and (pointer: fine) {
    .event-row .col-info:hover .team-name { color: var(--color-primary-light); }
  }*/

  /* ── Match info layout ────────────────────────────────────────────────────── */
  .match-info-container { flex-direction: row !important; }
  .info-body-block { display: flex; width: 100%; }
  .clickable-cell  { cursor: pointer; }

  /* ── Scoreboard ───────────────────────────────────────────────────────────── */
  .score-block { display: flex; flex-direction: column; align-items: flex-end; justify-content: center; min-width: auto; flex-shrink: 0; }
  .match-score { font-size: var(--font-size-5xl); font-weight: bold; color: var(--color-accent); white-space: nowrap; }
  .period-text { font-size: var(--font-size-md); color: var(--text-op-50-platform); font-weight: 600; text-align: right; white-space: nowrap; }

  /* ── Live status bar ──────────────────────────────────────────────────────── */
  /* Override global width:350px (stylenew.css .match-top-row) */
  .match-time {
    width: 100%; font-size: var(--font-size-xl); color: var(--text-op-50-platform);
    margin-bottom: 0; display: flex; justify-content: space-between; align-items: center;
  }
  .match-time div { display: flex; align-items: center; }

  /* ── Live / finished badges ───────────────────────────────────────────────── */
  .match-finished-badge {
    display: inline-block; font-size: var(--font-size-md); font-weight: 700; letter-spacing: 0.4px;
    text-transform: uppercase; color: var(--color-primary); border: 1px solid var(--color-primary);
    border-radius: 3px; padding: 1px 4px;
  }
  .event-row--finished { opacity: 0.5; pointer-events: none; }

  /* ── Incident badges ──────────────────────────────────────────────────────── */
  .goal-ping { background-color: var(--goal-bg) !important; border: 1px solid var(--goal-border) !important; color: var(--goal-border); border-radius: 4px; padding: 0 4px; font-size: var(--font-size-xxs); line-height: 12px; animation: ping 3s infinite; }
  .var-ping  { background-color: var(--var-bg)  !important; border: 1px solid var(--var-border)  !important; color: var(--var-border);  border-radius: 0;    padding: 0 4px; font-size: var(--font-size-xxs); line-height: 12px; animation: ping 3s infinite; }
  @keyframes ping { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }

  /* ── Odds blink animations ────────────────────────────────────────────────── */
  .blink_me { animation: blinker 1s linear infinite; }
  @keyframes blinker { 50% { opacity: 0; } }

  /* ── Corner triangle indicators ───────────────────────────────────────────── */

  /* ── Tournament header row ────────────────────────────────────────────────── */
  .path-container { font-weight: 700; color: var(--text-op-50-platform); display: flex; align-items: center; justify-content: flex-start; overflow: hidden; min-width: 0; flex: 1; white-space: nowrap; }
  .path-container img { width: 18px; margin-right: 8px; flex-shrink: 0; }
  .tournament-text {font-weight: 700; overflow: hidden; text-overflow: ellipsis; flex: 1; }

  /* ── Section dividers ─────────────────────────────────────────────────────── */
  .meup-section { border-top: 1px solid var(--border-color);margin-bottom: 40px }
  .meup-section:first-of-type { border-top: none; }

  .matches-list { position: relative; }

  /* ── Prematch section overrides (mirrors MarketViewV3 sizing) ─────────────── */
  .prematch-section .event-row          { min-height: 50px; display: flex; flex-direction: column; }
  .prematch-section .meta-block         { font-size: var(--font-size-xl); color: var(--color-accent);font-weight: bolder; }
  .prematch-section .match-info-container { cursor: pointer; }
  .prematch-section .competitors-block  { gap: 8px; }
  .prematch-section .col-more .value    { background-color: transparent; color: var(--text-op-50-platform); border: none; font-size: var(--font-size-xl); font-weight: bold; }

  /* ── Responsive overrides ─────────────────────────────────────────────────── */
  @media (max-width: 600px) {
    /* Live section keeps 64px row / 44px boxes (MarketLivePreview baseline) */
    .meup-section:not(.prematch-section) .event-row  { min-height: 64px; }
    .meup-section:not(.prematch-section) .outcome-box { height: 44px; width: 100%; display: flex; justify-content: center; align-items: center; }

    /* Prematch section keeps 44px row (MarketViewV3 mobile baseline) */
    .prematch-section .event-row { min-height: 44px; padding: 4px 0; }

    .grid-cell { padding: 0 6px; }
    .col-info  { padding: 0 8px; }
    .prematch-section .meta-block{
      justify-content: flex-end;
      align-items: flex-start;
    }

  }
</style>
